import { AppShell, Burger, Group, NavLink, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface NavItem {
	label: string;
	path: string;
	icon?: ReactNode;
	onClick?: () => void;
}

export interface NavSection {
	title?: string;
	items: NavItem[];
}

export interface PlatformAppShellProps {
	/** Main content to render */
	children: ReactNode;

	/** Navigation sections for sidebar. If not provided, no navbar will be shown */
	navigation?: NavSection[];

	/** Logo component to show in header */
	logo?: ReactNode;

	/** Right-side content for header (e.g., user menu) */
	headerRight?: ReactNode;

	/** Left-side content for header (shown after burger/logo) */
	headerLeft?: ReactNode;

	/** Header height in pixels */
	headerHeight?: number;

	/** Navbar width in pixels */
	navbarWidth?: number;

	/** Breakpoint for mobile/desktop (sm, md, lg, xl) */
	navbarBreakpoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

	/** Whether navbar should be open by default on desktop */
	navbarDefaultOpened?: boolean;

	/** Custom function to determine if a path is active */
	isActivePath?: (path: string, currentPath: string) => boolean;

	/** Whether to show burger menu on mobile when navbar exists */
	showBurger?: boolean;

	/** Callback when navigation item is clicked */
	onNavigate?: (path: string) => void;
}

export function PlatformAppShell({
	children,
	navigation,
	logo,
	headerRight,
	headerLeft,
	headerHeight = 60,
	navbarWidth = 280,
	navbarBreakpoint = 'sm',
	navbarDefaultOpened = true,
	isActivePath: customIsActivePath,
	showBurger = true,
	onNavigate,
}: PlatformAppShellProps) {
	const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
	const [desktopOpened] = useDisclosure(navbarDefaultOpened);
	const location = useLocation();
	const navigate = useNavigate();

	const defaultIsActivePath = (path: string, currentPath: string) => {
		if (path === '/' || path === '/dashboard') return currentPath === path;
		return currentPath.startsWith(path);
	};

	const checkActivePath = customIsActivePath || defaultIsActivePath;

	const handleNavigate = (item: NavItem) => {
		if (item.onClick) {
			item.onClick();
		} else {
			navigate(item.path);
		}

		if (onNavigate) {
			onNavigate(item.path);
		}

		// Close mobile nav after navigation
		if (mobileOpened) {
			toggleMobile();
		}
	};

	const hasNavbar = navigation && navigation.length > 0;

	return (
		<AppShell
			header={{ height: headerHeight }}
			navbar={
				hasNavbar
					? {
							width: navbarWidth,
							breakpoint: navbarBreakpoint,
							collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
						}
					: undefined
			}
			padding={0}
			style={{
				height: '100vh',
				overflow: 'hidden',
			}}
		>
			<AppShell.Header>
				<Group h="100%" px="md" justify="space-between">
					<Group>
						{hasNavbar && showBurger && (
							<Burger
								opened={mobileOpened}
								onClick={toggleMobile}
								hiddenFrom={navbarBreakpoint}
								size="sm"
							/>
						)}
						{logo}
						{headerLeft}
					</Group>
					{headerRight}
				</Group>
			</AppShell.Header>

			{hasNavbar && (
				<AppShell.Navbar p="md">
					<Stack gap="lg">
						{navigation.map((section, idx) => (
							<Stack key={section.title || idx} gap="xs">
								{section.title && (
									<Text size="xs" fw={600} c="dimmed" tt="uppercase" px="xs">
										{section.title}
									</Text>
								)}
								{section.items.map((item) => (
									<NavLink
										key={item.path}
										label={item.label}
										leftSection={item.icon}
										active={checkActivePath(item.path, location.pathname)}
										onClick={() => handleNavigate(item)}
										style={{ borderRadius: '8px' }}
									/>
								))}
							</Stack>
						))}
					</Stack>
				</AppShell.Navbar>
			)}

			<AppShell.Main
				style={{
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
				}}
			>
				{children}
			</AppShell.Main>
		</AppShell>
	);
}
